/**
 * Sep-design PDP gallery lightbox (replaces fancybox for the main slider images).
 *
 * A full-screen copy of the main slider: black overlay, prev / next, close, swipe to change
 * image, pinch / double-tap / click / wheel zoom and drag to pan. It only shows the slides the
 * main slider is showing (slides hidden for other colors are left out), and on close the main
 * slider moves to the image that was last viewed.
 *
 * Gallery images opt in with [data-sep-zoom] (data-src = large image, data-caption = alt).
 * Delegated from document because mobile hoists .mainslider-wrapper out of the gallery root
 * and the storefront renderer swaps gallery markup after load. The overlay markup is only
 * created on first open.
 */
;(function () {
  if (window.__sepGalleryLightbox) return
  window.__sepGalleryLightbox = true

  const MAX_SCALE = 4
  const DOUBLE_TAP_SCALE = 2.5
  const SWIPE_DISTANCE = 50
  const TAP_SLOP = 10

  const CSS =
    '.sep-lb{position:fixed;inset:0;z-index:2147483647;background:#000;color:#fff;touch-action:none;user-select:none;-webkit-user-select:none;overscroll-behavior:contain;opacity:0;transition:opacity .2s ease}' +
    '.sep-lb.is-open{opacity:1}' +
    '.sep-lb[hidden]{display:none}' +
    '.sep-lb__track{display:flex;height:100%;will-change:transform}' +
    '.sep-lb__track.is-animating{transition:transform .3s cubic-bezier(.22,.61,.36,1)}' +
    '.sep-lb__slide{flex:0 0 100%;height:100%;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:56px 0}' +
    '.sep-lb__img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;transform-origin:50% 50%;cursor:zoom-in;-webkit-user-drag:none;pointer-events:auto}' +
    '.sep-lb__img.is-animating{transition:transform .25s ease}' +
    '.sep-lb.is-zoomed .sep-lb__img{cursor:grab}' +
    '.sep-lb.is-dragging .sep-lb__img{cursor:grabbing}' +
    '.sep-lb__btn{position:absolute;display:flex;align-items:center;justify-content:center;width:48px;height:48px;padding:0;border:0;border-radius:50%;background:rgba(0,0,0,.5);color:#fff;cursor:pointer;z-index:2}' +
    '.sep-lb__btn:focus-visible{outline:2px solid #fff;outline-offset:2px}' +
    '.sep-lb__btn[disabled]{opacity:.3;cursor:default}' +
    '.sep-lb__btn svg{width:22px;height:22px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}' +
    '.sep-lb__close{top:8px;right:8px}' +
    '.sep-lb__prev{left:8px;top:50%;margin-top:-24px}' +
    '.sep-lb__next{right:8px;top:50%;margin-top:-24px}' +
    '.sep-lb__count{position:absolute;top:20px;left:16px;font-size:13px;letter-spacing:.05em;opacity:.8;pointer-events:none}' +
    '.sep-lb.is-single .sep-lb__prev,.sep-lb.is-single .sep-lb__next,.sep-lb.is-single .sep-lb__count{display:none}' +
    '@media (hover:none){.sep-lb__prev,.sep-lb__next{display:none}}' +
    'html.sep-lb-lock,html.sep-lb-lock body{overflow:hidden}' +
    '[data-sep-zoom]{cursor:zoom-in}'

  const ICONS = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>'
  }

  const style = document.createElement('style')
  style.textContent = CSS
  document.head.appendChild(style)

  let root, track, countEl, prevBtn, nextBtn, closeBtn
  let items = [] // { src, alt, slideIndex }
  let slides = [] // lightbox slide elements
  let index = 0
  let swiper = null
  let lastFocus = null
  let scale = 1, tx = 0, ty = 0

  // Gesture state
  const pointers = new Map()
  let gesture = null
  let lastTap = { time: 0, x: 0, y: 0 }

  function build() {
    root = document.createElement('div')
    root.className = 'sep-lb'
    root.hidden = true
    root.setAttribute('role', 'dialog')
    root.setAttribute('aria-modal', 'true')
    root.setAttribute('aria-label', 'Product images')
    root.innerHTML =
      '<div class="sep-lb__track"></div>' +
      '<div class="sep-lb__count" aria-live="polite"></div>' +
      '<button type="button" class="sep-lb__btn sep-lb__prev" aria-label="Previous image">' + ICONS.prev + '</button>' +
      '<button type="button" class="sep-lb__btn sep-lb__next" aria-label="Next image">' + ICONS.next + '</button>' +
      '<button type="button" class="sep-lb__btn sep-lb__close" aria-label="Close">' + ICONS.close + '</button>'
    document.body.appendChild(root)

    track = root.querySelector('.sep-lb__track')
    countEl = root.querySelector('.sep-lb__count')
    prevBtn = root.querySelector('.sep-lb__prev')
    nextBtn = root.querySelector('.sep-lb__next')
    closeBtn = root.querySelector('.sep-lb__close')

    prevBtn.addEventListener('click', () => go(index - 1))
    nextBtn.addEventListener('click', () => go(index + 1))
    closeBtn.addEventListener('click', close)

    root.addEventListener('pointerdown', onPointerDown)
    root.addEventListener('pointermove', onPointerMove)
    root.addEventListener('pointerup', onPointerUp)
    root.addEventListener('pointercancel', onPointerUp)
    root.addEventListener('wheel', onWheel, { passive: false })
    document.addEventListener('keydown', onKeydown)
    root.addEventListener('dragstart', e => e.preventDefault())
    window.addEventListener('resize', () => { if (!root.hidden) { resetZoom(false); positionTrack(false) } })
  }

  // ---------- open / close ----------

  function visibleSlides(swiperEl) {
    const all = Array.from(swiperEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide'))
    return all
      .map((slide, slideIndex) => ({ slide, slideIndex }))
      .filter(({ slide }) => !slide.classList.contains('hidden') && slide.getClientRects().length > 0)
  }

  function open(wrapper) {
    const swiperEl = wrapper.closest('.swiper')
    if (!swiperEl) return
    swiper = swiperEl.swiper || null

    items = []
    let start = 0
    visibleSlides(swiperEl).forEach(({ slide, slideIndex }) => {
      const zoomEl = slide.querySelector('[data-sep-zoom]')
      if (!zoomEl) return // videos keep their own player
      if (zoomEl === wrapper) start = items.length
      const img = zoomEl.querySelector('img')
      items.push({
        src: zoomEl.getAttribute('data-src') || (img && (img.currentSrc || img.src)) || '',
        preview: (img && (img.currentSrc || img.src)) || '',
        alt: zoomEl.getAttribute('data-caption') || (img && img.alt) || '',
        slideIndex
      })
    })
    if (!items.length) return

    if (!root) build()
    // Cookie banner, Foursixty, Patrol etc. also sit at the max z-index; being last in body wins the tie
    if (root !== document.body.lastElementChild) document.body.appendChild(root)
    track.innerHTML = ''
    slides = items.map(item => {
      const slide = document.createElement('div')
      slide.className = 'sep-lb__slide'
      const img = document.createElement('img')
      img.className = 'sep-lb__img'
      img.alt = item.alt
      img.draggable = false
      img.decoding = 'async'
      slide.appendChild(img)
      track.appendChild(slide)
      return slide
    })

    lastFocus = document.activeElement
    root.classList.toggle('is-single', items.length < 2)
    root.hidden = false
    document.documentElement.classList.add('sep-lb-lock')
    index = start
    resetZoom(false)
    positionTrack(false)
    update()
    requestAnimationFrame(() => root.classList.add('is-open'))
    closeBtn.focus({ preventScroll: true })
  }

  function close() {
    if (!root || root.hidden) return
    root.classList.remove('is-open')
    root.hidden = true
    document.documentElement.classList.remove('sep-lb-lock')
    pointers.clear()
    gesture = null
    // Leave the main slider on the image that was last viewed
    const item = items[index]
    if (swiper && item && typeof swiper.slideTo === 'function' && swiper.activeIndex !== item.slideIndex) {
      swiper.slideTo(item.slideIndex, 0)
    }
    track.innerHTML = ''
    slides = []
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true })
  }

  // ---------- slides ----------

  function load(i) {
    const slide = slides[i]
    if (!slide) return
    const img = slide.firstChild
    if (img.getAttribute('src')) return
    const item = items[i]
    // Show the image the page already has, then swap in the large one once it has loaded
    if (item.preview && item.preview !== item.src) {
      img.src = item.preview
      const full = new Image()
      full.decoding = 'async'
      full.onload = () => { if (img.isConnected) img.src = item.src }
      full.src = item.src
    } else {
      img.src = item.src
    }
  }

  function update() {
    load(index)
    load(index - 1)
    load(index + 1)
    countEl.textContent = (index + 1) + ' / ' + items.length
    prevBtn.disabled = index === 0
    nextBtn.disabled = index === items.length - 1
  }

  function positionTrack(animate, offset) {
    track.classList.toggle('is-animating', !!animate)
    track.style.transform = 'translate3d(calc(' + (-index * 100) + '% + ' + (offset || 0) + 'px),0,0)'
  }

  function go(i) {
    if (i < 0 || i >= items.length) { positionTrack(true); return }
    if (i !== index) resetZoom(false)
    index = i
    positionTrack(true)
    update()
  }

  // ---------- zoom / pan ----------

  function currentImg() {
    const slide = slides[index]
    return slide && slide.firstChild
  }

  function applyTransform(animate) {
    const img = currentImg()
    if (!img) return
    img.classList.toggle('is-animating', !!animate)
    img.style.transform = scale === 1 ? '' : 'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + scale + ')'
    root.classList.toggle('is-zoomed', scale > 1)
  }

  function clampPan() {
    const img = currentImg()
    if (!img) return
    const slide = slides[index]
    const maxX = Math.max(0, (img.offsetWidth * scale - slide.clientWidth) / 2)
    const maxY = Math.max(0, (img.offsetHeight * scale - slide.clientHeight) / 2)
    tx = Math.min(maxX, Math.max(-maxX, tx))
    ty = Math.min(maxY, Math.max(-maxY, ty))
  }

  // Point relative to the centre of the current slide
  function fromCentre(clientX, clientY) {
    const rect = slides[index].getBoundingClientRect()
    return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) }
  }

  // Zoom to newScale keeping the screen point (clientX, clientY) still
  function zoomAt(newScale, clientX, clientY, animate) {
    newScale = Math.min(MAX_SCALE, Math.max(1, newScale))
    const p = fromCentre(clientX, clientY)
    tx = p.x - (p.x - tx) * (newScale / scale)
    ty = p.y - (p.y - ty) * (newScale / scale)
    scale = newScale
    if (scale === 1) { tx = 0; ty = 0 }
    clampPan()
    applyTransform(animate)
  }

  function resetZoom(animate) {
    scale = 1
    tx = 0
    ty = 0
    applyTransform(animate)
  }

  function toggleZoom(clientX, clientY) {
    if (scale > 1) resetZoom(true)
    else zoomAt(DOUBLE_TAP_SCALE, clientX, clientY, true)
  }

  // ---------- gestures ----------

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  function onPointerDown(e) {
    if (e.target.closest('.sep-lb__btn')) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    try { root.setPointerCapture(e.pointerId) } catch (err) {}

    if (pointers.size === 2) {
      const [a, b] = Array.from(pointers.values())
      gesture = {
        type: 'pinch',
        startDist: distance(a, b) || 1,
        startScale: scale,
        moved: true
      }
      positionTrack(true) // drop any half-done swipe
      return
    }
    if (pointers.size > 2) return

    gesture = {
      type: scale > 1 ? 'pan' : 'swipe',
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      startTime: Date.now(),
      onImage: e.target === currentImg(),
      moved: false
    }
    root.classList.add('is-dragging')
  }

  function onPointerMove(e) {
    if (!pointers.has(e.pointerId) || !gesture) return
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (gesture.type === 'pinch') {
      if (pointers.size < 2) return
      const [a, b] = Array.from(pointers.values())
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      zoomAt(gesture.startScale * (distance(a, b) / gesture.startDist), mid.x, mid.y, false)
      return
    }

    const dx = e.clientX - gesture.startX
    const dy = e.clientY - gesture.startY
    if (!gesture.moved && Math.hypot(dx, dy) > TAP_SLOP) gesture.moved = true
    if (!gesture.moved) return

    if (gesture.type === 'pan') {
      tx += e.clientX - gesture.lastX
      ty += e.clientY - gesture.lastY
      clampPan()
      applyTransform(false)
    } else {
      // Resist past the first / last image
      const atEdge = (index === 0 && dx > 0) || (index === items.length - 1 && dx < 0)
      positionTrack(false, atEdge ? dx / 3 : dx)
    }
    gesture.lastX = e.clientX
    gesture.lastY = e.clientY
  }

  function onPointerUp(e) {
    if (!pointers.has(e.pointerId)) return
    pointers.delete(e.pointerId)
    const g = gesture

    if (g && g.type === 'pinch') {
      if (pointers.size === 1) {
        // One finger left: carry on as a pan from where it is
        const [p] = Array.from(pointers.values())
        gesture = { type: scale > 1 ? 'pan' : 'swipe', startX: p.x, startY: p.y, lastX: p.x, lastY: p.y, startTime: Date.now(), moved: true, fromPinch: true }
        if (gesture.type === 'swipe') gesture.type = 'none'
      } else if (pointers.size === 0) {
        gesture = null
        root.classList.remove('is-dragging')
        if (scale < 1.05) resetZoom(true)
      }
      return
    }
    if (pointers.size > 0) return
    gesture = null
    root.classList.remove('is-dragging')
    if (!g || g.type === 'none') return

    if (g.type === 'swipe' && g.moved) {
      const dx = e.clientX - g.startX
      const fast = Math.abs(dx) > 20 && Date.now() - g.startTime < 250
      if (dx < -SWIPE_DISTANCE || (fast && dx < 0)) go(index + 1)
      else if (dx > SWIPE_DISTANCE || (fast && dx > 0)) go(index - 1)
      else positionTrack(true)
      return
    }
    if (g.moved) return

    // Tap / click
    if (e.pointerType === 'mouse') {
      if (g.onImage) toggleZoom(e.clientX, e.clientY)
      else if (scale === 1) close()
      return
    }
    const now = Date.now()
    if (now - lastTap.time < 300 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
      lastTap.time = 0
      toggleZoom(e.clientX, e.clientY)
    } else {
      lastTap = { time: now, x: e.clientX, y: e.clientY }
    }
  }

  function onWheel(e) {
    e.preventDefault()
    const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002))
    zoomAt(scale * factor, e.clientX, e.clientY, false)
  }

  function onKeydown(e) {
    if (!root || root.hidden) return
    if (e.key === 'Escape') { e.preventDefault(); close() }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1) }
    else if (e.key === 'Tab') {
      // Keep focus inside the dialog
      const focusable = [prevBtn, nextBtn, closeBtn].filter(b => !b.disabled && b.offsetParent !== null)
      if (!focusable.length) return
      const i = focusable.indexOf(document.activeElement)
      const nextI = e.shiftKey ? (i <= 0 ? focusable.length - 1 : i - 1) : (i === focusable.length - 1 ? 0 : i + 1)
      e.preventDefault()
      focusable[nextI].focus()
    }
  }

  // ---------- open from the gallery ----------

  document.addEventListener('click', e => {
    const wrapper = e.target.closest && e.target.closest('[data-sep-zoom]')
    if (!wrapper) return
    const slide = wrapper.closest('.swiper-slide')
    if (slide && slide.classList.contains('hidden')) return
    const swiperEl = wrapper.closest('.swiper')
    // Swiper blocks clicks after a drag itself; this covers a click that slips through mid-transition
    if (swiperEl && swiperEl.swiper && swiperEl.swiper.allowClick === false) return
    e.preventDefault()
    open(wrapper)
  })

  window.SepGalleryLightbox = { open, close }
})()
